import React, { useContext, useMemo } from 'react';
import {
    isFading,
    isFilled,
    isCancelledOrRejected,
    OpenSubmissionsStreamItem,
} from '../store/stream/OpenSubmissionsStreamDataState';
import './OpenSubmissionsGrid.scss';
import {
    autoSizeVenueNameColumn,
    buildOpenSubmissionsColDefs,
    OpenSubmissionsColumn,
} from 'widgets/openSubmissions/grid/OpenSubmissionsColumn';
import { Grid, GridApi } from '@instinet-analytics/semantic-ui-components';
import { useWidgetGridState } from 'widgets/store/grid/useWidgetGridState';

import AppContext from 'app/AppContext';
import { GetRowClassParams } from '@instinet-analytics/semantic-ui-components';
import venueTypeCellRenderer from 'widgets/openSubmissions/grid/VenueTypeCellRenderer';
import { useSelectedOrderCurrency } from 'widgets/openSubmissions/grid/useSelectedOrderCurrency';
import { useSelectPortfolioStreamedOrSelectedOrders } from 'portfolio/store/hooks';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

export interface OpenSubmissionsGridProps {
    widgetKey: string;
    height: number;
    width: number;
    data: RowDataItem[];
    onGridApiSet: (gridApi: GridApi) => void;
}

const rowHeight = 25;

export interface RowDataItem extends OpenSubmissionsStreamItem {
    filled: number;
}

export default function OpenSubmissionsGrid(props: OpenSubmissionsGridProps) {
    const { data, height, width, onGridApiSet } = props;

    const context = useContext(AppContext);

    const orderCurrency = useSelectedOrderCurrency();
    const useWidgetEditedColumnNamesProps = useWidgetEditedColumnNames();

    // To show this component the selection must be a position from some orders with the same symbol, so we can extract the tickSize from the first order
    const orders = useSelectPortfolioStreamedOrSelectedOrders();
    const order = (orders ?? [])[0];
    const tickSize = order?.tickSize;

    const { columnDefinitions, colDefs } = useMemo(
        () => buildOpenSubmissionsColDefs(context, orderCurrency, tickSize),
        [context, orderCurrency, tickSize]
    );

    const onGridReady = (api: GridApi) => {
        onGridApiSet(api);
    };

    const { gridPropsFromState, columnDefs } = useWidgetGridState<
        RowDataItem,
        OpenSubmissionsColumn
    >(columnDefinitions, colDefs);

    autoSizeVenueNameColumn(columnDefs, width);

    const getRowClass = () => 'open-submission-row';

    const components = {
        venueType: venueTypeCellRenderer,
    };

    return (
        <div className="OpenSubmissionsGrid" style={{ height }}>
            <Grid
                className={'open-submissions-grid ag-theme-balham-dark'}
                rowHeight={rowHeight}
                headerHeight={rowHeight}
                columnDefs={columnDefs}
                customStyling={true}
                components={components}
                rowData={data}
                getRowClass={getRowClass}
                rowClassRules={getRowClassRules()}
                getRowDataId={getRowDataId}
                suppressColumnVirtualisation={false}
                onGridReady={onGridReady}
                {...gridPropsFromState}
                {...useWidgetEditedColumnNamesProps}
            />
        </div>
    );
}

function getRowDataId(d: RowDataItem) {
    return d._i.toString();
}

// Adding appropriate classNames to the row initiates the associated animations
function getRowClassRules() {
    return {
        cancelled: (params: GetRowClassParams<RowDataItem>) => {
            const item: RowDataItem = params.data;
            return !!item.shouldRemove && isCancelledOrRejected(item);
        },
        finished: (params: GetRowClassParams<RowDataItem>) => {
            const item: RowDataItem = params.data;
            return !!item.shouldRemove && isFilled(item);
        },
        fading: (params: GetRowClassParams<RowDataItem>) => {
            const item: RowDataItem = params.data;
            return (
                isFading(item) ||
                (!!item.shouldRemove &&
                    !isCancelledOrRejected(item) &&
                    !isFilled(item))
            );
        },
    };
}
